import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { getTier1, getTier2, unlockReport, accessByToken } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import ScoreCard from "@/components/ScoreCard";
import SubScoresPanel from "@/components/SubScoresPanel";
import FirstMove from "@/components/FirstMove";
import RestOfPlan from "@/components/RestOfPlan";
import EmailGate from "@/components/EmailGate";
import QueryStack from "@/components/QueryStack";
import TopicalClustersHeatmap from "@/components/TopicalClustersHeatmap";
import LiftDashboard from "@/components/LiftDashboard";
import { Lock, Trophy, AlertTriangle, Share2, Sparkles } from "lucide-react";

export default function Report() {
  const { analysisId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tier1, setTier1] = useState(null);
  const [tier2, setTier2] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState("");
  const [nextAction, setNextAction] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      // If ?t=<token> is present, redeem it FIRST so the analysis is
      // flagged unlocked before we pull tier1. Anyone with the URL gets in
      // (shareable-link model — matches Google Docs / Notion). Strip the
      // token from the URL after redemption so it doesn't linger in browser
      // history / share buttons.
      const token = searchParams.get("t");
      if (token) {
        try {
          await accessByToken(token);
        } catch (err) {
          console.warn("access token redemption failed:", err);
          // Fall through — user can still email-gate manually.
        }
        setSearchParams({}, { replace: true });
      }
      const t1 = await getTier1(analysisId);
      setTier1(t1);
      if (t1.unlocked) {
        const t2 = await getTier2(analysisId);
        setTier2(t2);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  const onUnlock = async (form) => {
    setUnlockError("");
    setUnlocking(true);
    try {
      const res = await unlockReport({ analysis_id: analysisId, ...form });
      setNextAction(res.recommended_next_action);
      // Reload with Tier 2 data
      const t1 = await getTier1(analysisId);
      setTier1(t1);
      const t2 = await getTier2(analysisId);
      setTier2(t2);
    } catch (e) {
      setUnlockError(e?.response?.data?.detail || "Could not unlock report.");
    } finally {
      setUnlocking(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <div className="glass rounded-2xl p-12 text-center text-[color:var(--text-secondary)]">
          Loading report…
        </div>
      </div>
    );
  }

  if (!tier1) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24">
        <div className="glass rounded-2xl p-12 text-center">
          <div className="font-serif text-2xl mb-3">Report not found</div>
          <div className="text-[color:var(--text-secondary)]">
            This analysis may not exist or may not have completed yet.
          </div>
        </div>
      </div>
    );
  }

  const prop = tier1.property;

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8 md:py-12">
      {/* Header */}
      <div className="mb-8 fade-in">
        <div className="flex items-center gap-3 label-eyebrow mb-3">
          <span>FieldGuide Local Authority Report</span>
          <span className="text-[color:var(--text-muted)]">·</span>
          <span className="text-[color:var(--text-muted)]">
            {new Date(tier1.completed_at).toLocaleDateString(undefined, { dateStyle: "medium" })}
          </span>
        </div>
        <h1 className="font-serif text-3xl md:text-5xl leading-tight" data-testid="report-property-name">
          {prop.name}
        </h1>
        <div className="text-[color:var(--text-secondary)] mt-2">
          {[prop.city, prop.state, prop.country].filter(Boolean).join(", ")}
          {prop.website && (
            <>
              {" · "}
              <a
                href={prop.website}
                target="_blank"
                rel="noreferrer"
                className="underline underline-offset-4 transition-colors"
                style={{ color: "var(--text-secondary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--brand-sage-deep)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--text-secondary)")}
              >
                {prop.website.replace(/^https?:\/\//, "")}
              </a>
            </>
          )}
        </div>
      </div>

      {/* Score card */}
      <ScoreCard
        tier1={tier1}
        evidence={tier1.unlocked && tier2 ? { queries: tier2.queries, platformResults: tier2.platform_results } : null}
      />

      {/* Property-specific authority interpretation */}
      {tier1.authority_interpretation && (
        <div
          className="mt-6 rounded-2xl px-6 md:px-8 py-5 md:py-6"
          style={{
            background: "var(--bg-card-warm)",
            border: "1px solid var(--border-default)",
          }}
          data-testid="report-interpretation"
        >
          <p
            className="font-serif text-lg md:text-xl leading-relaxed"
            style={{ color: "var(--text-primary)" }}
          >
            {tier1.authority_interpretation}
          </p>
        </div>
      )}

      {/* Query stacks — where you appear vs. where you don't */}
      <div className="grid lg:grid-cols-2 gap-4 md:gap-6 mt-6">
        <QueryStack
          eyebrow="Where you appear"
          eyebrowColor="var(--score-high)"
          icon={Trophy}
          items={tier1.query_stacks?.appeared || []}
          emptyMessage="Your property wasn't clearly mentioned in any of the tested queries — a signal that content ownership is the biggest opportunity."
          renderMeta={(it) =>
            it.platforms?.length
              ? `Mentioned on ${it.platforms.length} of 3 platforms · ${it.platforms.join(", ")}`
              : null
          }
          testId="report-stack-appeared"
        />
        <QueryStack
          eyebrow="Where you don't"
          eyebrowColor="var(--score-low)"
          icon={AlertTriangle}
          items={tier1.query_stacks?.absent || []}
          emptyMessage="Nice — your property appears in every query we tested. Room to grow is now about depth and consistency."
          renderMeta={(it) => (it.category ? `Category: ${it.category}` : null)}
          testId="report-stack-absent"
        />
      </div>

      {/* Your Visibility Gap — replaces "The Uncomfortable Truth" */}
      <div
        className="mt-6 rounded-2xl p-6 md:p-8 relative overflow-hidden"
        style={{
          borderColor: "rgba(107,122,85,0.35)",
          border: "1px solid rgba(107,122,85,0.35)",
          background: "linear-gradient(135deg, rgba(107,122,85,0.08), rgba(166,124,63,0.04))",
        }}
        data-testid={TESTIDS.reportDramaticStat}
      >
        <div className="label-eyebrow mb-3">Your visibility gap</div>
        <div
          className="font-serif text-2xl md:text-3xl leading-tight mb-4"
          style={{ color: "var(--text-primary)" }}
        >
          {tier1.dramatic_stat}
        </div>
        {tier1.visibility_stats && (
          <p
            className="text-base md:text-lg leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
            data-testid="report-visibility-comparison"
          >
            By way of comparison, your property&apos;s own content played that role in{" "}
            <span
              className="font-medium"
              style={{ color: "var(--text-primary)" }}
            >
              {tier1.visibility_stats.own_pct}%
            </span>{" "}
            of the conversations tested.
          </p>
        )}
      </div>

      {/* Topical clusters heatmap — per-category rollup + FieldGuide lever */}
      <TopicalClustersHeatmap clusters={tier1.topical_clusters} />

      {/* Share bar */}
      <div className="mt-6 flex items-center gap-3">
        <button
          className="btn-ghost text-sm !py-2 !px-4"
          data-testid="report-share-btn"
          onClick={() => {
            navigator.clipboard?.writeText(window.location.href);
          }}
        >
          <Share2 className="w-4 h-4" />
          Copy report link
        </button>
        <span className="text-xs text-[color:var(--text-muted)]">
          Public URL — share this with your team.
        </span>
      </div>

      {/* Tier 2 or gate */}
      {tier1.unlocked && tier2 ? (
        <UnlockedReport
          tier2={tier2}
          nextAction={nextAction}
          score={tier1.knownfor_score}
          analysisId={analysisId}
        />
      ) : (
        <>
          {/* Opportunity bridge — positive framing before the ask */}
          <div
            className="mt-10 rounded-2xl p-6 md:p-8"
            style={{
              background:
                "linear-gradient(135deg, rgba(107,122,85,0.12), rgba(166,124,63,0.06))",
              border: "1px solid rgba(107,122,85,0.35)",
            }}
            data-testid="report-opportunity-bridge"
          >
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4" style={{ color: "var(--brand-sage)" }} />
              <div className="label-eyebrow" style={{ color: "var(--brand-sage-deep)" }}>
                The good news
              </div>
            </div>
            <p
              className="font-serif text-xl md:text-2xl leading-snug mb-2"
              style={{ color: "var(--text-primary)" }}
            >
              Your property&apos;s content gaps are specific and addressable.
            </p>
            <p
              className="text-base leading-relaxed"
              style={{ color: "var(--text-secondary)" }}
            >
              We&apos;ve identified the queries {prop.name} is best positioned to answer,
              and developed a plan to build the content assets that will help you improve
              your score.
            </p>
          </div>

          <EmailGate onSubmit={onUnlock} loading={unlocking} error={unlockError} />
        </>
      )}
    </div>
  );
}


function UnlockedReport({ tier2, analysisId }) {
  const a = tier2.analysis;
  const firstRec = tier2.recommendations?.[0];
  const propertyCity = a.property_snapshot?.city;

  return (
    <div className="mt-14 fade-in" data-testid={TESTIDS.reportTier2Section}>
      <div className="mb-6 flex items-center gap-3">
        <Lock className="w-4 h-4" style={{ color: "var(--brand-sage)" }} />
        <div className="label-eyebrow" style={{ color: "var(--brand-sage-deep)" }}>
          Full report unlocked
        </div>
      </div>

      {/* Chapter 1 — Diagnosis */}
      <div className="mb-4">
        <div className="label-eyebrow mb-1">Chapter 1 · Your diagnosis</div>
        <div className="font-serif text-2xl">The four dimensions of your Local Authority</div>
      </div>
      <SubScoresPanel subScores={a.sub_scores} opportunityLevel={a.opportunity_level} />

      {/* Score-Lift Dashboard — only renders for properties with ≥2 completed runs */}
      <LiftDashboard analysisId={analysisId} />

      {/* The First Move */}
      <div className="mt-16">
        <FirstMove
          recommendation={firstRec}
          analysisId={analysisId}
          propertyCity={propertyCity}
        />
      </div>

      {/* Rest of the plan — collapsed */}
      <div className="mt-8">
        <RestOfPlan recommendations={tier2.recommendations} />
      </div>
    </div>
  );
}
